import './practical.scss'

import {AddressType, LinkType} from 'campagne/types'
import {format, isValid} from 'date-fns'
import React, {FunctionComponent, ReactElement} from 'react'
import {
	AlertGreenIcon,
	CalendarSmallIcon,
	LocationSmallIcon,
	PersonIcon
} from 'styles/inline-icons'
import {style} from 'util/campagne/style'
import {useTranslation} from 'util/i18n'
import {Theme} from 'views/campagne/theme'
import {ButtonSecondary} from 'views/ui/Button'

type TimeType = {
	from: number
	until?: number
}

type OwnerType = string

type PublicType = boolean

export type PracticalType = {
	address?: AddressType
	owner?: OwnerType
	isPublic?: PublicType
	email?: string
	subscriptionRequired?: boolean
	start: Date
	end: Date
	hasEndHour: boolean
	hasEndDate: boolean
	external_url?: string
	subscription_details?: LinkType
}

const styles = style('practical', ['item', 'raster'])

export const Practical: FunctionComponent<PracticalType> = ({
	address,
	owner,
	isPublic,
	email,
	subscriptionRequired = false,
	start,
	end,
	hasEndHour,
	hasEndDate,
	external_url,
	subscription_details
}) => {
	const t = useTranslation('campagne').info
	const {street, number: useNumber, city, zip, box = null, province} = address

	return (
		<div class={styles}>
			<div class={styles.raster} />
			<div class={styles('container')}>
				<Theme.H3
					mod={['slantedtoleft', 'marked', 'white']}
					class={styles('container-title')}
				>
					{t.practical}
				</Theme.H3>
				<div class={styles('content')}>
					{isPublic && start && (
						<PracticalItem icon={<CalendarSmallIcon />}>
							<Theme.P>
								{hasEndDate ? t.from : t.on} {format(start, 'dd/MM')}
								{hasEndDate &&
									isValid(end) &&
									' ' + t.until + ' ' + format(end, 'dd/MM')}
							</Theme.P>

							<Theme.P>
								{hasEndHour ? t.between : t.at} {format(start, 'HH:mm')}
								{hasEndHour &&
									isValid(end) &&
									' ' + t.and + ' ' + format(end, 'HH:mm')}
							</Theme.P>
						</PracticalItem>
					)}
					{isPublic && (street || city || province) && (
						<PracticalItem icon={<LocationSmallIcon />}>
							<Theme.P>{`${street} ${useNumber} ${
								box && '(' + t.box + ' ' + box + ')'
							}`}</Theme.P>
							<Theme.P>{`${zip} ${city}`}</Theme.P>
							<Theme.P>{province}</Theme.P>
						</PracticalItem>
					)}
					{owner && (
						<PracticalItem icon={<PersonIcon />}>
							<Theme.P>{owner}</Theme.P>
						</PracticalItem>
					)}
					<PracticalItem icon={<AlertGreenIcon />}>
						<Theme.P>{isPublic ? t.public : t.not_public}</Theme.P>
					</PracticalItem>
					{subscriptionRequired && (subscription_details || email) && (
						<div class={styles.item.with('signup')}>
							<Theme.P>{t.sign_up}</Theme.P>
							{subscription_details ? (
								<Theme.Link to={subscription_details.url}>
									{subscription_details.url}
								</Theme.Link>
							) : (
								<Theme.Link to={`mailto:${email}`}>{email}</Theme.Link>
							)}
						</div>
					)}
				</div>
				{external_url && external_url !== '' && (
					<ButtonSecondary to={external_url}>{t.moreinfo}</ButtonSecondary>
				)}
			</div>
		</div>
	)
}
export const PracticalPromotion: FunctionComponent = ({children}) => {
	const t = useTranslation('campagne').info
	return (
		<div class={styles}>
			<div class={styles.raster} />
			<div class={styles('container')}>{children}</div>
		</div>
	)
}

export const PracticalItem: FunctionComponent<{
	icon: ReactElement
}> = ({icon, children}) => (
	<div class={styles.item}>
		<span class={styles.item('icon')}>{icon}</span>
		{children}
	</div>
)
